import { useState, useEffect } from 'react';
import WizardModal, { WizardButton, WizardLabel, WizardInput, WizardError } from './WizardModal';
import { post, get } from '../../api';

interface ShoppingItem {
  name: string;
  amount: number;
  unit: string;
  category: string;
}

interface ShoppingResult {
  list_id: number;
  items: ShoppingItem[];
  normalized: boolean;
  message?: string;
}

type Step = 'input' | 'result';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ShoppingWizard({ open, onClose }: Props) {
  const [dateFrom, setDateFrom] = useState(nextMonday());
  const [dateTo, setDateTo] = useState(nextSunday());
  const [ignorePantry, setIgnorePantry] = useState(false);
  const [persons, setPersons] = useState(1);
  const [step, setStep] = useState<Step>('input');
  const [result, setResult] = useState<ShoppingResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingBring, setSendingBring] = useState(false);
  const [bringSent, setBringSent] = useState(false);

  // Load household_size from profile on open
  useEffect(() => {
    if (open) {
      get<{ household_size?: number }>('/profile').then(p => {
        if (p.household_size) setPersons(p.household_size);
      }).catch(() => {});
    }
  }, [open]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await post<ShoppingResult>('/ai/normalize-shopping', {
        date_from: dateFrom,
        date_to: dateTo,
        ignore_pantry: ignorePantry,
        persons_override: persons > 1 ? persons : undefined,
      });
      setResult(data);
      setStep('result');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Einkaufsliste konnte nicht erstellt werden');
    } finally {
      setLoading(false);
    }
  };

  const handleSendToBring = async () => {
    if (!result?.list_id) return;
    setSendingBring(true);
    try {
      await post(`/shopping/${result.list_id}/send-to-bring`, {});
      setBringSent(true);
    } catch {
      // Non-critical
    } finally {
      setSendingBring(false);
    }
  };

  const handleClose = () => {
    setStep('input');
    setResult(null);
    setError(null);
    setBringSent(false);
    onClose();
  };

  // ── Result view ──
  if (step === 'result' && result) {
    // Group by category
    const grouped = new Map<string, ShoppingItem[]>();
    for (const item of result.items) {
      const list = grouped.get(item.category) || [];
      list.push(item);
      grouped.set(item.category, list);
    }

    return (
      <WizardModal
        open={open}
        onClose={handleClose}
        title="Einkaufsliste"
        footer={
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors"
            >
              Fertig
            </button>
            <button
              onClick={handleSendToBring}
              disabled={sendingBring || bringSent}
              className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-medium text-sm hover:bg-orange-600 disabled:opacity-50 transition-colors"
            >
              {bringSent ? 'Gesendet!' : sendingBring ? 'Sende...' : 'An Bring! senden'}
            </button>
          </div>
        }
      >
        {result.items.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            {result.message || 'Keine Zutaten gefunden. Hast du einen Plan für diesen Zeitraum?'}
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-gray-400">{result.items.length} Zutaten, {dateFrom} bis {dateTo}</p>
            {[...grouped.entries()].map(([category, items]) => (
              <div key={category}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{category}</p>
                <div className="space-y-0.5">
                  {items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-1.5">
                      <span className="text-sm text-gray-700">{item.name}</span>
                      <span className="text-xs text-gray-400 shrink-0 ml-2">
                        {item.amount} {item.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </WizardModal>
    );
  }

  // ── Input view ──
  return (
    <WizardModal
      open={open}
      onClose={handleClose}
      title="Einkaufsliste erstellen"
      footer={
        <WizardButton onClick={handleGenerate} loading={loading}>
          Liste erstellen
        </WizardButton>
      }
    >
      {error && <WizardError message={error} />}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <WizardLabel>Von</WizardLabel>
          <WizardInput type="date" value={dateFrom} onChange={setDateFrom} />
        </div>
        <div>
          <WizardLabel>Bis</WizardLabel>
          <WizardInput type="date" value={dateTo} onChange={setDateTo} />
        </div>
      </div>

      {/* Persons stepper */}
      <div>
        <WizardLabel>Für wie viele Personen?</WizardLabel>
        <div className="flex items-center gap-3 mt-1">
          <button onClick={() => setPersons(Math.max(1, persons - 1))}
            className="w-9 h-9 rounded-lg bg-warm-100 text-warm-600 flex items-center justify-center text-lg font-medium">-</button>
          <span className="text-xl font-display font-semibold text-warm-800 w-8 text-center">{persons}</span>
          <button onClick={() => setPersons(Math.min(10, persons + 1))}
            className="w-9 h-9 rounded-lg bg-warm-100 text-warm-600 flex items-center justify-center text-lg font-medium">+</button>
          <span className="text-xs text-warm-400">{persons === 1 ? 'Person' : 'Personen'}</span>
        </div>
      </div>

      <button
        onClick={() => setIgnorePantry(!ignorePantry)}
        className="w-full flex items-start gap-3 text-left"
      >
        <div className={`mt-0.5 w-10 h-6 rounded-full transition-colors shrink-0 relative ${ignorePantry ? 'bg-emerald-500' : 'bg-gray-300'}`}>
          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${ignorePantry ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-800">Vorratskammer ignorieren</p>
          <p className="text-[11px] text-gray-400 leading-tight">Alle Zutaten auflisten, auch wenn vorhanden</p>
        </div>
      </button>
    </WizardModal>
  );
}

function nextMonday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + diff);
  return d.toLocaleDateString('sv-SE');
}

function nextSunday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? 7 : 14 - day;
  d.setDate(d.getDate() + diff);
  return d.toLocaleDateString('sv-SE');
}
